import React, { useState, useEffect } from 'react';
import { Modal, Card, Row, Col, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { formatDate } from '../utils/dataUtils';
import { apiService } from '../services/api';
import '../styles/BrandDetail.css';

const BrandDetail = ({ show, onHide, categoryId, brandId, categoryName, brandName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [brandDetails, setBrandDetails] = useState(null);
  
  // Keep only the essentials, removing the stats state
  useEffect(() => {
    let mounted = true;

    const fetchDetails = async () => {
      if (!categoryId || !brandId) return;
      
      try {
        setLoading(true);
        const details = await apiService.fetchBrandDetails(
          categoryId, 
          brandId, 
          categoryName, 
          brandName
        );
        if (mounted) {
          setBrandDetails(details);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(`Failed to load details for ${brandName}`);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (show && categoryId && brandId) {
      fetchDetails();
    }

    return () => {
      mounted = false;
    };
  }, [show, categoryId, brandId, categoryName, brandName]);

  // Helper function to safely format numbers
  const safeFormat = (value) => {
    return value !== undefined && value !== null 
      ? value.toLocaleString() 
      : 'N/A';
  };
  
  // Helper function to format date only (no time)
  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (err) {
      return 'Invalid Date';
    }
  };

  // Helper function to determine platform icon based on account name
  const getPlatformIcon = (accountName) => {
    const accountNameLower = accountName.toLowerCase();
    
    if (accountNameLower.includes('facebook') || accountNameLower.includes('fb')) {
      return <i className="fab fa-facebook platform-icon"></i>;
    } else if (accountNameLower.includes('instagram') || accountNameLower.includes('ig')) {
      return <i className="fab fa-instagram platform-icon"></i>;
    } else if (accountNameLower.includes('twitter') || accountNameLower.includes('x.com')) {
      return <i className="fab fa-twitter platform-icon"></i>;
    } else if (accountNameLower.includes('youtube') || accountNameLower.includes('yt')) {
      return <i className="fab fa-youtube platform-icon"></i>;
    } else if (accountNameLower.includes('linkedin') || accountNameLower.includes('li')) {
      return <i className="fab fa-linkedin platform-icon"></i>;
    } else if (accountNameLower.includes('tiktok') || accountNameLower.includes('tt')) {
      return <i className="fab fa-tiktok platform-icon"></i>;
    } else if (accountNameLower.includes('pinterest') || accountNameLower.includes('pin')) {
      return <i className="fab fa-pinterest platform-icon"></i>;
    } else {
      return <i className="fas fa-globe platform-icon"></i>;
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered
      className="brand-detail-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-building me-2 text-primary"></i>
          {brandName} Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : brandDetails ? (
          <div className="brand-details-container">
            {/* Removed the analytics section with statistics */}
            
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 d-flex align-items-center">
                    <i className="fas fa-users me-2 text-primary"></i>
                    Accounts & Pages
                  </h5>
                  {brandDetails.stats_updated_date && (
                    <div className="stats-update-info">
                      <i className="fas fa-clock me-1"></i>
                      Stats updated: {formatDateOnly(brandDetails.stats_updated_date)}
                    </div>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                {brandDetails.accounts && brandDetails.accounts.length > 0 ? (
                  <div className="accounts-table-container">
                    <Table responsive hover className="accounts-table">
                      <thead>
                        <tr>
                          <th>Platform</th>
                          <th>Account Name</th>
                          <th>Status</th>
                          <th>Page Stats</th>
                          <th>Posts</th>
                          <th>Comments</th>
                          <th>Last Updated</th>
                          <th>Page Stats Update</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brandDetails.accounts.map((account, index) => (
                          <tr key={account.account_id || index}>
                            <td>
                              {getPlatformIcon(account.account_name || '')}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <span className="account-name">
                                  {account.account_name || 'Unnamed Account'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <Badge bg={account.is_outdated ? "danger" : "success"}>
                                {account.is_outdated ? "Outdated" : "Up to date"}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={account.is_validated ? "success" : "warning"} className="validation-badge">
                                {account.is_validated ? (
                                  <><i className="fas fa-check-circle me-1"></i> Validated</>
                                ) : (
                                  <><i className="fas fa-clock me-1"></i> Pending</>
                                )}
                              </Badge>
                            </td>
                            <td>{safeFormat(account.brandpost)}</td>
                            <td>{safeFormat(account.comments)}</td>
                            <td>{account.last_datafetchedinduration ? 
                                 formatDate(account.last_datafetchedinduration) : 'N/A'}</td>
                            <td>{account.stats_updated_date ? 
                                 formatDateOnly(account.stats_updated_date) : 
                                 (brandDetails.stats_updated_date ? formatDateOnly(brandDetails.stats_updated_date) : 'N/A')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="info">No accounts found for this brand.</Alert>
                )}
              </Card.Body>
            </Card>
          </div>
        ) : (
          <Alert variant="info">No account details available for this brand.</Alert>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default BrandDetail;
